import { defineComponent, computed, reactive, ref, SetupContext } from '@vue/composition-api';
import { TableHeader, ProcessingStatusOrganizationsDetailList, ConfigIconCoordinate, OnColumns } from '@/shared/type';
import HttpStatus from 'http-status-codes';
import CommonMessageDialog from '../common/common-message-dialog.component.vue';
import CommonOnColumnsMenu from '@/components/common/common-on-columns-menu.component.vue';
import SystemConst from '@/shared/system-const';
import vueRouter from '@/router/index';                                 // ルート情報取得

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    processingStatusList: ProcessingStatusOrganizationsDetailList[];
    headers: TableHeader[];
    isLoading: boolean;
};

/**
 *  処理状況詳細コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, CommonOnColumnsMenu },
    setup(props, ctx: SetupContext) {
        const tableHeight = SystemConst.TABLE_HEIGHT.PROCESSING_STATUS_ORGANIZATIONS_DETAIL;   // テーブル高さ調整用変数
        const router = vueRouter;                                                              // 画面遷移用
        const tableHeaderClass: string[] = ['secondary subtitle-2'];                           // ヘッダ色
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                               // テーブルのデータ取得中に表示するテキスト
        const commonRef = ref<any>(null);                                                      // メッセージダイアログ
        const columnRef = ref<any>(null);                                                      // 列設定ダイアログ
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },
            processingStatusList: [],
            headers: [],
            isLoading: false,                                                                  // テーブルがロード中: true ロード中でない: false
        });
        const path = computed(() => ctx.root.$route.params.coverage);                          // 親画面のパラメータ（中間処理ID）取得
        const errorIcon: TableHeader[] = [                                                     // エラーアイコン
            {
                text: '',
                value: 'isError',
                width: '40px',
                sortable: false,
                class: tableHeaderClass
            }
        ];
        const processingStatusTableInitHeaders: TableHeader[] = [                              // 処理状況詳細ヘッダ情報（初期表示用）
            // 組織ID
            {
                text: '組織ID',
                value: 'organizationId',
                width: '120px',
                class: tableHeaderClass
            },
            // 組織型
            {
                text: '組織型',
                value: 'organizationTypeName',
                width: '200px',
                class: tableHeaderClass
            },
            // 組織名称
            {
                text: '組織名称',
                value: 'organizationName',
                width: '180px',
                class: tableHeaderClass
            },
            // 組織ドメイン
            {
                text: '組織ドメイン',
                value: 'domain',
                width: '180px',
                class: tableHeaderClass
            },
            // 処理内容
            {
                text: '処理内容',
                value: 'processCodeName',
                width: '110px',
                class: tableHeaderClass
            },
            // 処理状況
            {
                text: '処理状況',
                value: 'statusCodeName',
                width: '130px',
                class: tableHeaderClass
            },
            // 実行結果
            {
                text: '実行結果',
                value: 'result',
                width: '250px',
                class: tableHeaderClass
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                width: '4%',
                sortable: false,
                class: tableHeaderClass,
                align: 'end'
            }
        ];
        const processingStatusTableHeaders: TableHeader[] = [                   // 処理状況詳細ヘッダ情報
            // 組織ID
            {
                text: '組織ID',
                value: 'organizationId',
                width: '120px',
                class: tableHeaderClass
            },
            // 所属コード
            {
                text: '所属コード',
                value: 'affiliationCode',
                width: '150px',
                class: tableHeaderClass
            },
            // 表示順
            {
                text: '表示順',
                value: 'displayOrder',
                width: '100px',
                class: tableHeaderClass
            },
            // 地域
            {
                text: '地域',
                value: 'locationName',
                width: '150px',
                class: tableHeaderClass
            },
            // 組織型
            {
                text: '組織型',
                value: 'organizationTypeName',
                width: '200px',
                class: tableHeaderClass
            },
            // 組織名称
            {
                text: '組織名称',
                value: 'organizationName',
                width: '180px',
                class: tableHeaderClass
            },
            // 組織名称（かな）
            {
                text: '組織名称（かな）',
                value: 'organizationNameKana',
                width: '180px',
                class: tableHeaderClass
            },
            // 組織ドメイン
            {
                text: '組織ドメイン',
                value: 'domain',
                width: '180px',
                class: tableHeaderClass
            },
            // 代表メールアドレス
            {
                text: '代表メールアドレス',
                value: 'representMail',
                width: '180px',
                class: tableHeaderClass
            },
            // グループ作成
            {
                text: 'グループ作成',
                value: 'groupName',
                width: '180px',
                class: tableHeaderClass
            },
            // CMS利用可否
            {
                text: 'CMS利用可否',
                value: 'canCmsName',
                width: '180px',
                class: tableHeaderClass
            },
            // 処理内容
            {
                text: '処理内容',
                value: 'processCodeName',
                width: '110px',
                class: tableHeaderClass
            },
            // 処理状況
            {
                text: '処理状況',
                value: 'statusCodeName',
                width: '130px',
                class: tableHeaderClass
            },
            // 実行結果
            {
                text: '実行結果',
                value: 'result',
                width: '250px',
                class: tableHeaderClass
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                width: '4%',
                sortable: false,
                class: tableHeaderClass,
                align: 'end'
            }
        ];

        const onColumns: OnColumns[] = [                                        // 列設定情報
            {
                text: '全て表示',
                value: false
            },
            {
                text: '組織ID',
                value: true
            },
            {
                text: '所属コード',
                value: false
            },
            {
                text: '表示順',
                value: false
            },
            {
                text: '地域',
                value: false
            },
            {
                text: '組織型',
                value: true
            },
            {
                text: '組織名称',
                value: true
            },
            {
                text: '組織名称（かな）',
                value: false
            },
            {
                text: '組織ドメイン',
                value: true
            },
            {
                text: '代表メールアドレス',
                value: false
            },
            {
                text: 'グループ作成',
                value: false
            },
            {
                text: 'CMS利用可否',
                value: false
            },
            {
                text: '処理内容',
                value: true
            },
            {
                text: '処理状況',
                value: true
            },
            {
                text: '実行結果',
                value: true
            },
        ];

        created();
        /**
         * 初期表示
         */
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-list-status');

            // タイトル表示
            ctx.root.$store.commit('setMainTitle', '処理状況詳細');

            state.headers = errorIcon.concat(processingStatusTableInitHeaders);

            // 処理状況詳細一覧を表示する
            updateList();
        }

        /**
         * 処理状況詳細一覧の表示
         */
        async function updateList() {

            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // 処理状況詳細一覧取得APIでデータを取得する
            //  GET /api/processing-status-organizations/中間処理ID
            const params = {
                processId: path.value
            }
            const response = await ctx.root.$store.dispatch('processingStatusOrganizations/getDetail', params);

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する。
                state.isLoading = false;
                // HTTPステータスコードが404の場合、"指定された処理が存在しない"
                // 旨のメッセージを表示して親画面へ遷移する。
                if (response.status === HttpStatus.NOT_FOUND) {
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/processing-status-organizations/list');
                } else {
                    // HTTPステータスコードが400, 404以外はエラー画面へ遷移して処理を終了する。
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // 処理状況詳細一覧のデータをstate.processingStatusListへ設定する
            const processingList = [...response.data.processingStatusOrganizations];
            processingList.forEach(e => {
                e.groupName = [e.canSalaryGroupName, e.publicRelationsGroupName, e.officialDocumentGroupName].filter(x => x).join('、');
            });

            state.processingStatusList = processingList;

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * 列設定アイコンをクリックした際に呼ばれるイベント
         */
        function showMenu() {
            const configIconCoordinate: ConfigIconCoordinate = { x: null, y: null };
            configIconCoordinate.x = document.querySelector('#target');
            configIconCoordinate.y = document.querySelector('#target');
            columnRef.value.showMenu(configIconCoordinate);
        }

        /**
         * 列設定を実施した際に呼ばれるイベント
         * 
         * @param displayTableHeaders 選択されたヘッダ一覧
         */
        function getTableHeader(displayTableHeaders: TableHeader[]) {
            state.headers = errorIcon.concat(displayTableHeaders);
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        /**
         * 戻るボタン押下時の処理
         */
        function back() {
            router.push('/processing-status-organizations/list');
        }

        // テンプレートへ公開
        return {
            tableHeight,
            state,
            processingStatusTableHeaders,
            processingStatusTableInitHeaders,
            tableLoadingText,
            commonRef,
            columnRef,
            onColumns,
            showMenu,
            getTableHeader,
            onResize,
            back
        };
    }
});
